import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import { authAPI } from '../services/api';
import AuthLayout from '../components/AuthLayout';
import AuthField from '../components/AuthField';
import FormError from '../components/FormError';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [resendAt, setResendAt] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [resent, setResent] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => { if (success) titleRef.current?.focus(); }, [success]);
  useEffect(() => {
    const update = () => setSecondsLeft(Math.max(0, Math.ceil((resendAt - Date.now()) / 1000)));
    update();
    if (!resendAt) return undefined;
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [resendAt]);

  const sendLink = async () => {
    if (loading || (success && Date.now() < resendAt)) return;
    setLoading(true); setError(''); setResent(false);
    try {
      await authAPI.forgotPassword(email.trim());
      setEmail(email.trim());
      setResent(success);
      setSuccess(true);
      setResendAt(Date.now() + 60000);
    } catch (err) {
      setError(!err.response
        ? 'Cannot reach the server. Check your connection and try again.'
        : err.response.status === 429
          ? 'Too many requests. Please wait a few minutes before trying again.'
          : 'We could not send the reset email. Please try again shortly.');
    } finally { setLoading(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const input = e.currentTarget.elements['forgotpassword-email'];
    if (!email.trim() || !input.validity.valid) {
      setEmailError('Enter a valid email address.'); input.focus(); return;
    }
    sendLink();
  };

  return (
    <AuthLayout title={success ? 'Check your email' : 'Forgot your password?'} titleRef={titleRef}
      description={success ? 'Your next step is in your inbox.' : 'Enter the email you use for LeadMarka. We’ll send you a link to choose a new password.'}>
      {success ? (
        <div className="space-y-5">
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
            <Mail aria-hidden="true" className="mb-2 h-6 w-6 text-primary-700" />
            <p className="text-sm leading-6 text-gray-700">If there’s a LeadMarka account for <strong className="break-words">{email}</strong>, you’ll receive a reset link. It expires in 1 hour.</p>
          </div>
          <p className="text-sm leading-6 text-gray-600">Allow a few minutes for delivery and check your spam or junk folder. Look for “Reset your LeadMarka password”.</p>
          <FormError message={error} />
          <button type="button" onClick={sendLink} disabled={loading || secondsLeft > 0}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60">
            {loading && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
            {loading ? 'Sending...' : secondsLeft > 0 ? `Resend link in ${secondsLeft}s` : 'Resend reset link'}
          </button>
          <button type="button" disabled={loading} onClick={() => { setSuccess(false); setError(''); setResent(false); }}
            className="flex w-full items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-700">Use a different email</button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-5" aria-busy={loading}>
          <FormError message={error} />
          <AuthField id="forgotpassword-email" label="Email" type="email" autoComplete="email" autoCapitalize="none" spellCheck={false}
            placeholder="you@example.com" required disabled={loading} value={email} error={emailError}
            onChange={(e) => { setEmail(e.target.value); setEmailError(''); }} />
          <button type="submit" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 p-3 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60">
            {loading && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
            {loading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>
      )}
      <p role="status" className="sr-only">{loading ? 'Sending reset email.' : resent ? 'Another reset email has been requested. Check your inbox.' : ''}</p>
      <div className="mt-6 border-t border-gray-200 pt-3 text-center">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700"><ArrowLeft aria-hidden="true" className="h-4 w-4" />Back to sign in</Link>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
